import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
interface CardProps {
  children: React.ReactNode;
  className?: string;
  href?: string;
  onClick?: () => void;
  padding?: 'none' | 'small' | 'medium' | 'large';
  rounded?: boolean;
  shadow?: 'none' | 'small' | 'medium' | 'large';
  border?: boolean;
  hover?: boolean;
  hoverEffect?: 'shadow' | 'scale' | 'border' | 'none';
  hoverLift?: boolean;
  background?: 'white' | 'gray' | 'blue' | 'yellow' | 'transparent';
  delay?: number;
}
const Card: React.FC<CardProps> = ({
  children,
  className = '',
  href,
  onClick,
  padding = 'medium',
  rounded = true,
  shadow = 'medium',
  border = false,
  hover = false,
  hoverEffect = 'shadow',
  hoverLift = false,
  background = 'white',
  delay = 0
}) => {
  // Padding styles
  const paddingStyles = {
    none: '',
    small: 'p-3',
    medium: 'p-6',
    large: 'p-8'
  };
  // Rounded styles
  const roundedStyles = rounded ? 'rounded-lg' : '';
  // Shadow styles
  const shadowStyles = {
    none: '',
    small: 'shadow-sm',
    medium: 'shadow-md',
    large: 'shadow-lg'
  };
  // Border styles
  const borderStyles = border ? 'border border-gray-200' : '';
  // Hover styles
  const hoverStyles = hover ? {
    shadow: 'hover:shadow-lg',
    scale: 'hover:scale-105',
    border: 'hover:border-blue-500',
    none: ''
  }[hoverEffect] : '';
  // Hover lift effect
  const hoverLiftStyles = hoverLift ? 'hover:-translate-y-1' : '';
  // Background styles
  const backgroundStyles = {
    white: 'bg-white',
    gray: 'bg-gray-50',
    blue: 'bg-blue-50',
    yellow: 'bg-yellow-50',
    transparent: 'bg-transparent'
  };
  // Combined styles
  const combinedStyles = `
    ${paddingStyles[padding]} 
    ${roundedStyles} 
    ${shadowStyles[shadow]} 
    ${borderStyles} 
    ${hoverStyles} 
    ${hoverLiftStyles} 
    ${backgroundStyles[background]}
    transition-all duration-300
    ${className}
  `;
  // Motion props
  const motionProps = {
    initial: {
      opacity: 0,
      y: 20
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5,
      delay: delay
    },
    whileHover: hover ? {
      y: hoverLift ? -4 : 0,
      scale: hoverEffect === 'scale' ? 1.05 : 1,
      boxShadow: hoverEffect === 'shadow' ? '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' : ''
    } : {}
  };
  // If href is provided, render as a Link
  if (href) {
    return <motion.div {...motionProps} data-id="element-591">
        <Link to={href} className={combinedStyles} data-id="element-592">
          {children}
        </Link>
      </motion.div>;
  }
  // Otherwise render as a div or button
  return onClick ? <motion.button onClick={onClick} className={combinedStyles} {...motionProps} data-id="element-593">
      {children}
    </motion.button> : <motion.div className={combinedStyles} {...motionProps} data-id="element-594">
      {children}
    </motion.div>;
};
export default Card;